import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Pagination,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/de";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import "./translations";

import { useGetServiceApiValuationPackageStatusQuery } from "@/api/enhancedGeneratedApi";
import { useGetServiceApiValuationPackageValuationsQuery } from "@/api/generatedApi";
import { SearchIcon } from "@/assets";
import { HousePlusIcon } from "@/assets/icons/HousePlusIcon";
import { HouseStarIcon } from "@/assets/icons/HouseStarIcon";
import { ProfileMaleIcon } from "@/assets/icons/ProfileMale";
import { BasePage } from "@/components";
import { CircularIconWrapper } from "@/components/circleIconsWrapper";
import { ObjectCategoryDropdown } from "@/components/objectCategoryDropdown";
import { TimeFilterDropdown, TimeFilterValue } from "@/components/timeFilterDropdown";
import { ValuationOwnerToggle } from "@/components/valuationOwnerToggle";
import { usePermissions } from "@/hooks/permissionsHooks/usePermissions";
import { useAppSelector } from "@/store/hooks";
import { ModuleType } from "@/types/ModuleType";
import { BuildingTypeEnum, ObjectTypeEnum } from "@/types/refineRealEstate";

import { AccessDeniedPage } from "../../error/accessDeniedPage";
import { EmptyValuationsList } from "./EmptyValuationState";
import { HeaderWithQuota } from "./HeaderWithQuota";
import { ValuationsList } from "./ValuationsList";

export type SortDirection = "Ascending" | "Descending";
export type SortableColumn = "Nation" | "Town" | "Street" | "PostCode" | "CreatedAt" | "AskingPrice";

const styles = {
  pageContainer: {
    height: "calc(100vh - 110px)",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  stackContainer: {
    height: "100%",
  },
  welcomeBox: {
    bgcolor: "white",
    p: 3,
  },
  createButton: {
    height: "45px",
    bgcolor: "primary.main",
    "&:hover": {
      bgcolor: "primary.main",
    },
  },
  valuationsBox: {
    display: "flex",
    flexDirection: "column",
    bgcolor: "white",
    p: 3,
    height: "100%",
    overflow: "hidden",
  },
  headerStack: {
    pb: 2,
  },
  searchIcon: {
    mr: 2,
    color: "#A4A7AE",
    fontSize: "15px",
  },
  searchInputProps: {
    height: "45px",
  },
  searchField: {
    maxWidth: "500px",
  },
  filtersBox: {
    display: "flex",
    gap: 2.5,
  },
  dropdownSelect: {
    width: "210px",
    height: "36px",
  },
  emptyStateBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "400px",
  },
  emptyStateContainer: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
  },
  paginationBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    pt: 2,
    flexShrink: 0,
  },
  tableWrapper: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
  },
  tableContainer: {
    flexGrow: 1,
    minHeight: 0,
    overflow: "hidden",
  },
};

const now = dayjs().locale("de");

const getPageSize = (): number => {
  const ROW_HEIGHT = 72;
  const NON_TABLE_HEIGHT = 520; // Total height of all UI except table rows (header, boxes, filters, pagination, etc.)

  const availableHeight = window.innerHeight - NON_TABLE_HEIGHT;
  const calculatedRows = Math.floor(availableHeight / ROW_HEIGHT);

  return Math.max(5, Math.min(calculatedRows, 20));
};

export const ValuationListPage = (): JSX.Element => {
  const { t } = useTranslation("ValuationListPage");
  const user = useAppSelector((state) => state.applicationMain?.userInfo?.user);
  const userPermissions = usePermissions();
  const { data: quotaStatus, isLoading: isQuotaLoading } = useGetServiceApiValuationPackageStatusQuery();
  const canViewInstitutionValuations = quotaStatus?.can_view_institution_valuations;

  const theme = useTheme();
  const navigate = useNavigate();

  const [sortBy, setSortBy] = useState<SortableColumn>("CreatedAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("Descending");

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showOnlyMine, setShowOnlyMine] = useState(!canViewInstitutionValuations);
  const [selectedObjectCategory, setSelectedObjectCategory] = useState<ObjectTypeEnum | null>(null);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState<TimeFilterValue | null>(null);

  const PAGE_SIZE = getPageSize();

  const getBuildingTypesForCategory = (category: ObjectTypeEnum | null | ""): Array<BuildingTypeEnum> | undefined => {
    if (!category) return undefined;
    switch (category) {
      case ObjectTypeEnum.house:
        return [
          BuildingTypeEnum.singleFamilyHouse,
          BuildingTypeEnum.singleFamilyHouseWithGrannyApartment,
          BuildingTypeEnum.twoFamilyHouse,
        ];
      case ObjectTypeEnum.apartment:
        return [BuildingTypeEnum.apartment];
      case ObjectTypeEnum.multiFamilyHouse:
        return [BuildingTypeEnum.multiFamilyHouse];
      case ObjectTypeEnum.mixedUseProperty:
        return [BuildingTypeEnum.mixedUseProperty];
      case ObjectTypeEnum.plotOfLand:
        return [BuildingTypeEnum.plotArea];
      default:
        return undefined;
    }
  };

  const createdAfterDate = useMemo(() => {
    if (!selectedTimeFilter) return undefined;

    const now = dayjs();
    switch (selectedTimeFilter) {
      case "thisMonth":
        return now.startOf("month").toISOString();
      case "lastMonth":
        return now.subtract(1, "month").startOf("month").toISOString();
      case "last3Months":
        return now.subtract(3, "month").startOf("month").toISOString();
      case "thisYear":
        return now.startOf("year").toISOString();
    }
  }, [selectedTimeFilter]);

  const createdBeforeDate = useMemo(() => {
    if (!selectedTimeFilter) return undefined;

    const now = dayjs();
    switch (selectedTimeFilter) {
      case "thisMonth":
        return undefined; // Current month - no end date
      case "lastMonth":
        return now.subtract(1, "month").endOf("month").toISOString();
      case "last3Months":
        return now.subtract(1, "month").endOf("month").toISOString(); // End of last month
      case "thisYear":
        return undefined; // This year - no end date
    }
  }, [selectedTimeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 1000);

    return (): void => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data: valuationsResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetServiceApiValuationPackageValuationsQuery({
    pageSize: PAGE_SIZE,
    pageIndex: currentPage - 1,
    sortDirection: sortDirection,
    orderBy: sortBy,
    showOnlyMine: showOnlyMine ?? undefined,
    buildingType: getBuildingTypesForCategory(selectedObjectCategory),
    createAtAfter: createdAfterDate,
    createAtBefore: createdBeforeDate,
    ...(debouncedSearchQuery && { addressSearchQuery: debouncedSearchQuery }),
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [showOnlyMine, debouncedSearchQuery, selectedObjectCategory, selectedTimeFilter, sortBy, sortDirection]);

  const handleDeleted = useCallback((): void => {
    refetch();
  }, [refetch]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number): void => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const valuations = valuationsResponse?.data || [];
  const emptyValuationList = valuations.length === 0;
  const hasActiveFilters = Boolean(debouncedSearchQuery || selectedObjectCategory || selectedTimeFilter);

  const totalPages = Math.ceil((valuationsResponse?.pagination?.total_records ?? 0) / PAGE_SIZE);

  if (!userPermissions.has(ModuleType.VALUATIONSPACKAGE)) {
    return <AccessDeniedPage />;
  }

  return (
    <BasePage
      showSidebar={false}
      customHeader={<HeaderWithQuota quotaStatus={quotaStatus} isLoadingQuota={isQuotaLoading} />}
    >
      <Box sx={styles.pageContainer}>
        <Stack spacing="20px" sx={styles.stackContainer}>
          <Box sx={styles.welcomeBox}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularIconWrapper size={"45px"} backgroundColor={alpha(theme.palette.primary.main, 0.16)}>
                  <ProfileMaleIcon sx={{ color: theme.palette.primary.main }} />
                </CircularIconWrapper>
                <Stack spacing={1}>
                  <Typography variant="menuTitle">
                    {`${t("welcome")}`}
                    {user?.name ? `, ${user?.name}!` : "!"}
                  </Typography>
                  <Typography variant="subText">{now.format("D. MMMM YYYY")}</Typography>
                </Stack>
              </Stack>
              <Button
                variant="contained"
                startIcon={<HousePlusIcon />}
                sx={styles.createButton}
                onClick={() => navigate("/valuations/new")}
                data-cy="valuation-list-create-new-valuation"
              >
                {t("createValuation")}
              </Button>
            </Stack>
          </Box>
          <Box sx={styles.valuationsBox}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={styles.headerStack}
            >
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <CircularIconWrapper size={"45px"} backgroundColor={alpha(theme.palette.primary.main, 0.16)}>
                  <HouseStarIcon sx={{ color: theme.palette.primary.main }} />
                </CircularIconWrapper>
                <Stack spacing={1}>
                  <Typography variant="menuTitle">{t("myRealEstateValuations")}</Typography>
                  <Typography variant="subText">{t("currentAndCompletedValuations")}</Typography>
                </Stack>
              </Stack>

              {(!emptyValuationList || hasActiveFilters) && (
                <TextField
                  placeholder={t("searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: <SearchIcon sx={styles.searchIcon} />,
                    endAdornment: (
                      <InputAdornment position="end">
                        {(isLoading || isFetching) && searchQuery !== "" ? (
                          <CircularProgress size={15} />
                        ) : searchQuery !== "" ? (
                          <IconButton size="small" onClick={() => setSearchQuery("")} edge="end">
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </InputAdornment>
                    ),
                    sx: styles.searchInputProps,
                  }}
                  sx={styles.searchField}
                  data-cy="address-search"
                />
              )}
            </Stack>

            {(!emptyValuationList || hasActiveFilters || canViewInstitutionValuations) && (
              <Box sx={styles.filtersBox}>
                {canViewInstitutionValuations && (
                  <ValuationOwnerToggle showOnlyMine={showOnlyMine} setShowOnlyMine={setShowOnlyMine} />
                )}

                <Box sx={{ flex: 1 }}></Box>
                <ObjectCategoryDropdown
                  value={selectedObjectCategory}
                  onChange={setSelectedObjectCategory}
                  selectSx={styles.dropdownSelect}
                />

                <TimeFilterDropdown
                  value={selectedTimeFilter}
                  onChange={setSelectedTimeFilter}
                  selectSx={styles.dropdownSelect}
                />
              </Box>
            )}

            {!isLoading && !isFetching && emptyValuationList && !hasActiveFilters && (
              <Box sx={styles.emptyStateContainer}>
                <EmptyValuationsList />
              </Box>
            )}
            {!isLoading && !isFetching && emptyValuationList && hasActiveFilters && (
              <Box sx={styles.emptyStateBox}>
                <Typography variant="sectionHeader">{t("noEntriesFound")}</Typography>
              </Box>
            )}

            {(!emptyValuationList || isLoading || isFetching) && (
              <Box sx={styles.tableWrapper}>
                <Box sx={styles.tableContainer} data-cy="valuation-package-valuations-list">
                  <ValuationsList
                    valuations={valuations}
                    onDeleted={handleDeleted}
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSortChange={(sortBy, direction) => {
                      setSortBy(sortBy);
                      setSortDirection(direction);
                    }}
                    isLoading={isLoading}
                    isFetching={isFetching}
                  />
                </Box>

                {totalPages > 1 && (
                  <Box sx={styles.paginationBox} data-cy="valuation-package-valuations-list-pagination">
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      disabled={isFetching}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    </BasePage>
  );
};
